import { Controller, useFormContext } from 'react-hook-form';
// Types
import { FormInputs } from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/CreateStreamSetup';
// Hooks
import { useEstimatedStreamProfit } from 'hooks';
import { useGetPpvTiers } from 'hooks/useGetPpvTiers';
// Helpers
import { getPlatformFeeCopy, getPlatformFeePercent } from 'helpers/platformFee';
import { formatPriceToUSD } from 'helpers/priceText';
// Ui
import Switch from 'ui3/Switch/Switch';
import Text from 'ui3/Text/Text';
// Components
import PriceTierSelector from 'components/common3/PriceTierSelector/PriceTierSelector';
import SectionDivider from 'components/common3/SectionDivider/SectionDivider';
import StreamOptionRow from 'components/common3/StreamOptionRow/StreamOptionRow';
import InfoTooltip from 'components/ManageAma/Campaigns/common/InfoTooltip/InfoTooltip';
import AffiliateProgramSection from 'components/ProfileSetup/components/common/AffiliateProgramSection/AffiliateProgramSection';
import WizardStepLayout from 'components/ProfileSetup/components/common/CreateStreamWizard/WizardStepLayout/WizardStepLayout';
import StreamSummaryPanel from 'components/ProfileSetup/components/common/CreateStreamWizard/StreamSummaryPanel/StreamSummaryPanel';
// Styles
import styles from './PricingStep.module.scss';

const PricingStep = (): JSX.Element => {
  const {
    control,
    register,
    errors,
    watch,
    trigger,
  } = useFormContext<FormInputs>();
  const {
    tiers: ppvTiers,
    loading: ppvTiersLoading,
    error: ppvTiersError,
    refetch: refetchPpvTiers,
  } = useGetPpvTiers();

  const isFreeStream = Boolean(watch('isFree'));
  const price = Number(watch('price'));
  const netProfit = useEstimatedStreamProfit(price, isFreeStream);
  const showEarnings = price > 0 && netProfit > 0;

  return (
    <WizardStepLayout aside={<StreamSummaryPanel />}>
      <div className={styles.heading}>
        <Text variant="h4">Pricing & Access</Text>
        <Text variant="body1Regular16" color="lights-low">
          Choose what fans pay to watch this PPV event.
        </Text>
      </div>

      <div className={styles.pricing}>
        <StreamOptionRow
          className={styles.freeCard}
          title="Make this Stream Free"
          description="Run this stream as free for fans."
        >
          <Switch
            wrapperClassName={styles.switch}
            name="isFree"
            ref={register}
          />
        </StreamOptionRow>

        {!isFreeStream && (
          <div className={styles.priceBlock}>
            <Text
              variant="body2Medium"
              color="lights-low"
              className={styles.priceLabel}
            >
              Stream price
            </Text>
            <div className={styles.priceField}>
              <Controller
                control={control}
                name="price"
                defaultValue=""
                render={({ onChange, value }) => (
                  <PriceTierSelector
                    tiers={ppvTiers}
                    value={value ?? ''}
                    onSelect={(selected) => {
                      onChange(String(selected));
                      trigger('price');
                    }}
                    loading={ppvTiersLoading}
                    error={ppvTiersError}
                    onRetry={() => refetchPpvTiers()}
                  />
                )}
              />
              {errors?.price?.message && (
                <Text variant="captionRegular" color="error-default">
                  {errors.price.message}
                </Text>
              )}
              {showEarnings && (
                <div className={styles.priceNotice}>
                  <InfoTooltip
                    title="Platform Fee"
                    text={getPlatformFeeCopy(
                      getPlatformFeePercent(price, netProfit)
                    )}
                    iconSize={16}
                    place="left"
                  />
                  <Text as="span" variant="captionRegular" color="lights-low">
                    You earn{' '}
                    <Text
                      as="span"
                      variant="body3Medium"
                      color="accent-default"
                    >
                      {formatPriceToUSD(netProfit)}
                    </Text>
                    .
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <SectionDivider />

      <AffiliateProgramSection netProfit={netProfit} disabled={isFreeStream} />
    </WizardStepLayout>
  );
};

export default PricingStep;
